import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { DataNode } from '../../../types/types'

interface NodeMapStoreType {
    contentItemToNodeMap: Map<string, DataNode>
    isSaveOperationInProgress: boolean
    saveOperationKey: string
    elementsBeingAdded: Set<string>
    recentlyAddedElements: Map<string, number>
    isArrowNavigationActive: boolean
    pendingFocusNode: {
        nodeKey: string
        timestamp: number
    } | null
    setSaveOperationKey: (key: string) => void
    buildNodeMap: (treeData: DataNode[]) => void
    getNodeByContentId: (contentId: string) => DataNode | undefined
    setSaveOperationInProgress: (inProgress: boolean) => void
    markElementAsBeingAdded: (elementId: string) => void
    markElementAsAdded: (elementId: string) => void
    isElementBeingAdded: (elementId: string) => boolean
    isElementRecentlyAdded: (elementId: string) => boolean
    setIsArrowNavigationActive: (isActive: boolean) => void
    setPendingFocusNode: (nodeInfo: { nodeKey: string; timestamp: number } | null) => void
}

/**
 * Creates a zustand store for managing a mapping from contentItem.id to DataNode.
 *
 * ## Intent & Use Case
 * This store is designed to provide **fast, O(1) lookups** for finding the parent or associated DataNode
 * for any given content item in the workspace editor tree. In complex tree structures, it is often necessary
 * to quickly determine which node a particular content item belongs to—especially for operations like
 * keyboard navigation, content insertion, or contextual actions.
 *
 * By maintaining a Map of contentItem.id to DataNode, this store enables:
 * - Efficient parent node lookups by contentItem.id (no need to traverse the tree each time)
 * - Easy integration with React components via custom hooks
 * - Centralized, reactive state for node mapping, automatically updated when the tree changes
 *
 * ## Example Usage
 * - Quickly find the parent node for a given content item when handling keyboard shortcuts
 * - Build or rebuild the mapping whenever the tree data changes
 * - Use hooks to access node map actions or retrieve nodes by contentItem.id in any component
 *
 * @returns {NodeMapStoreType} A reactive store with node mapping methods
 */
export const useNodeMapStore = create<NodeMapStoreType>()((set, get) => ({
    contentItemToNodeMap: new Map<string, DataNode>(),
    isSaveOperationInProgress: false,
    saveOperationKey: '',
    elementsBeingAdded: new Set<string>(),
    recentlyAddedElements: new Map<string, number>(),
    isArrowNavigationActive: false,
    pendingFocusNode: null,

    buildNodeMap: (treeData: DataNode[]) => {
        const map = new Map<string, DataNode>()

        const traverse = (nodes: DataNode[]) => {
            nodes.forEach((node) => {
                node.contentItemNodes?.forEach((contentItem) => {
                    if (contentItem.id) {
                        map.set(contentItem.id, node)
                    }
                })
                if (node.children) {
                    traverse(node.children)
                }
            })
        }

        traverse(treeData)
        set({ contentItemToNodeMap: map })
    },

    getNodeByContentId: (contentId: string) => {
        return get().contentItemToNodeMap.get(contentId)
    },

    setSaveOperationInProgress: (inProgress: boolean) => {
        set({ isSaveOperationInProgress: inProgress })
    },
    setSaveOperationKey: (key: string) => {
        set({ saveOperationKey: key })
    },
    
    markElementAsBeingAdded: (elementId: string) => {
        set((state) => ({
            elementsBeingAdded: new Set(state.elementsBeingAdded).add(elementId)
        }))
    },
    
    markElementAsAdded: (elementId: string) => {
        set((state) => {
            const newSet = new Set(state.elementsBeingAdded)
            newSet.delete(elementId)
            // Mark as recently added with current timestamp
            const newRecentlyAdded = new Map(state.recentlyAddedElements)
            newRecentlyAdded.set(elementId, Date.now())
            return { 
                elementsBeingAdded: newSet,
                recentlyAddedElements: newRecentlyAdded
            }
        })
        // Clean up recently added elements after 2 seconds
        setTimeout(() => {
            set((state) => {
                const newRecentlyAdded = new Map(state.recentlyAddedElements)
                newRecentlyAdded.delete(elementId)
                return { recentlyAddedElements: newRecentlyAdded }
            })
        }, 2000)
    },
    
    isElementBeingAdded: (elementId: string) => {
        return get().elementsBeingAdded.has(elementId)
    },
    
    isElementRecentlyAdded: (elementId: string) => {
        const timestamp = get().recentlyAddedElements.get(elementId)
        if (!timestamp) return false
        // Consider element recently added if less than 2 seconds old
        return Date.now() - timestamp < 2000
    },
    
    setIsArrowNavigationActive: (isActive: boolean) => {
        set({ isArrowNavigationActive: isActive })
    },
    
    setPendingFocusNode: (nodeInfo: { nodeKey: string; timestamp: number } | null) => {
        set({ pendingFocusNode: nodeInfo })
    },
}))

/**
 * Custom hook that retrieves a specific node by contentItem.id
 * Uses shallow comparison to prevent unnecessary re-renders
 * @param contentId - The contentItem.id to lookup
 * @returns DataNode | undefined
 */
export const useNodeByContentId = (contentId: string): DataNode | undefined => {
    if (!contentId) return undefined

    const { contentItemToNodeMap } = useNodeMapShallowSelector((state) => ({
        contentItemToNodeMap: state.contentItemToNodeMap,
    }))
    return contentItemToNodeMap.get(contentId)
}

/**
 * Custom hook that provides access to node map operations
 * @returns Object with node map methods
 */
export const useNodeMapActions = () => {
    return useNodeMapStore(
        useShallow((state) => ({
            buildNodeMap: state.buildNodeMap,
            getNodeByContentId: state.getNodeByContentId,
            setSaveOperationInProgress: state.setSaveOperationInProgress,
            setSaveOperationKey: state.setSaveOperationKey,
            markElementAsBeingAdded: state.markElementAsBeingAdded,
            markElementAsAdded: state.markElementAsAdded,
            isElementBeingAdded: state.isElementBeingAdded,
            isElementRecentlyAdded: state.isElementRecentlyAdded,
            setIsArrowNavigationActive: state.setIsArrowNavigationActive,
            setPendingFocusNode: state.setPendingFocusNode,
        }))
    )
}

/**
 * A custom hook that retrieves a shallowly selected value from the `useNodeMapStore` store.
 *
 * Sample usage:
 * ```typescript
 * const { contentItemToNodeMap, buildNodeMap } = useNodeMapShallowSelector((state) => ({
 *   contentItemToNodeMap: state.contentItemToNodeMap,
 *   buildNodeMap: state.buildNodeMap,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useNodeMapShallowSelector = <T>(
    selector: (state: NodeMapStoreType) => T
): T => useNodeMapStore(useShallow(selector))
